import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportError } from '@src/utils/errorReporting';
import { awaitJSON, SlidesLambdaFetch } from '../../utils/fetch-utils';
import { PresentationStatus } from './presentation-status-constants';
import {
  getPresentationStatusRefetchInterval,
  hasPollLimitPassed
} from './presentation-status-utils';

const fetchPresentationStatus = async (jobId: string): Promise<PresentationStatus> => {
  const res = await SlidesLambdaFetch('/getPresentationStatus', { params: { jobId } });
  return awaitJSON<PresentationStatus>(res);
};

// The time of the last response, successful or failed. React Query sets each one to 0 until the
// first response of that kind.
const getLastResponseAt = (dataUpdatedAt: number, errorUpdatedAt: number) =>
  Math.max(dataUpdatedAt, errorUpdatedAt);

// Polls one slide generation job until it reaches a final status or a response arrives after
// PRESENTATION_STATUS_POLL_LIMIT_MS. A failed request does not stop polling. Polling stops when
// the caller unmounts.
export const usePresentationStatus = (jobId: string) => {
  const [pollStartedAt] = useState(() => Date.now());
  const errorReported = useRef(false);

  const { data, error, dataUpdatedAt, errorUpdatedAt } = useQuery({
    queryKey: ['presentationStatus', jobId],
    queryFn: () => fetchPresentationStatus(jobId),
    refetchInterval: ({ state }) =>
      getPresentationStatusRefetchInterval({
        status: state.data,
        pollLimitPassed: hasPollLimitPassed(
          pollStartedAt,
          getLastResponseAt(state.dataUpdatedAt, state.errorUpdatedAt)
        )
      }),
    // The dialog shows its own fallback text on an error, so no global error modal.
    meta: { suppressGlobalError: true }
  });

  // Polling continues after an error, so report only the first one of this dialog.
  useEffect(() => {
    if (error && !errorReported.current) {
      errorReported.current = true;
      reportError(error, { operation: 'getPresentationStatus' });
    }
  }, [error]);

  return {
    status: data,
    hasError: Boolean(error),
    pollLimitPassed: hasPollLimitPassed(
      pollStartedAt,
      getLastResponseAt(dataUpdatedAt, errorUpdatedAt)
    )
  };
};
